import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FaSpinner } from 'react-icons/fa';
import Select from 'react-select';

import { guestApi } from '../../api/guestList';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../context/AuthContext';

const EditGuestModal = ({ show, onHide, guest, groups, onGuestUpdated }) => {
  const { isAuthenticated } = useAuthContext();

  const [submitting, setSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({
    id: null,
    jina: '',
    selectedGroups: [],
    namba: '',
    pledge_amount: '',
    paid_amount: '',
    payment_amount: '',
    card_type: 'Single',
    email: ''
  });

  // Transform groups for dropdown - memoized to prevent unnecessary recreations
  const groupOptions = useMemo(() => {
    console.log('[EditGuestModal] Transforming groups:', groups);
    
    if (!groups || !Array.isArray(groups)) {
      console.log('[EditGuestModal] Groups is not an array');
      return [];
    }

    // Flatten groups if it's nested array
    let flatGroups = groups;
    if (groups.length > 0 && Array.isArray(groups[0])) {
      console.log('[EditGuestModal] Detected nested array, flattening...');
      flatGroups = groups.flat();
    }
    
    const options = flatGroups.map(group => {
      console.log('[EditGuestModal] Processing group:', group);
      return {
        value: group.id,
        label: group.group_name || group.name
      };
    });
    
    console.log('[EditGuestModal] Transformed groupOptions:', options);
    return options;
  }, [groups]);

  useEffect(() => {
    if (guest && show) {
      console.log('[EditGuestModal] useEffect - guest.groups:', guest.groups);
      console.log('[EditGuestModal] useEffect - available groupOptions:', groupOptions);
      
      // Match guest's groups with available options
      const initialGroups = [];
      if (guest.groups && Array.isArray(guest.groups) && guest.groups.length > 0) {
        guest.groups.forEach(guestGroup => {
          const guestGroupId = guestGroup.id || guestGroup;
          const matchedOption = groupOptions.find(g => g.value === guestGroupId);
          if (matchedOption) {
            initialGroups.push(matchedOption);
          }
        });
      }

      console.log('[EditGuestModal] Initial selected groups:', initialGroups);

      setEditForm({
        id: guest.id,
        jina: guest.jina || '',
        selectedGroups: initialGroups,
        namba: guest.namba || '',
        pledge_amount: guest.pledge_amount_raw ? guest.pledge_amount_raw.toString() : '0',
        paid_amount: guest.paid_amount_raw ? guest.paid_amount_raw.toString() : '0',
        payment_amount: '',
        card_type: guest.card_type || 'Single',
        email: guest.email || ''
      });
    }
  }, [guest, show, groupOptions]);

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let phoneStr = String(phone).trim();
    phoneStr = phoneStr.replace(/[^\d+]/g, '');
    if (!phoneStr.startsWith('255') && !phoneStr.startsWith('0')) {
      phoneStr = '0' + phoneStr;
    }
    return phoneStr;
  };

  const handleUpdateGuest = async () => {
    if (!editForm.id) {
      toast.error('No guest selected for update');
      return;
    }

    setSubmitting(true);
    try {
      const phoneNumber = formatPhoneNumber(editForm.namba);
      const currentPaidAmount = parseFloat(editForm.paid_amount) || 0;
      const newPaymentAmount = parseFloat(editForm.payment_amount) || 0;
      const totalPaidAmount = currentPaidAmount + newPaymentAmount;

      const updateResponse = await guestApi.updateGuest(editForm.id, {
        name: String(editForm.jina).trim(),
        phone_number: phoneNumber,
        email: editForm.email || null,
        pledge: parseFloat(editForm.pledge_amount) || 0,
        fund: newPaymentAmount, // Backend adds this, so we only send the new payment
        groups: editForm.selectedGroups.map(g => g.value),
        group_ids: editForm.selectedGroups.map(g => g.value),
        group_id: editForm.selectedGroups.length > 0 ? editForm.selectedGroups[0].value : null,
        guest_list_group_id: editForm.selectedGroups.length > 0 ? editForm.selectedGroups[0].value : null
      });

      if (updateResponse?.success === false) {
        toast.error(updateResponse.message || 'Failed to update guest');
        setSubmitting(false);
        return;
      }

      const successMessage = newPaymentAmount > 0
        ? `Guest updated successfully! Added TZS ${newPaymentAmount.toLocaleString()} to payments.`
        : `Guest updated successfully!`;

      toast.success(successMessage);
      onGuestUpdated();
      onHide();

    } catch (err) {
      console.error('Error updating guest:', err);
      const errMsg = err.response?.data?.message || err.message || 'Failed to update guest';
      toast.error(`${errMsg}`);
    } finally {
      setSubmitting(false);
    }
  };

  
  if (!isAuthenticated) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Hariri Taarifa za Mgeni</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Jina *</Form.Label>
                <Form.Control
                  type="text"
                  value={editForm.jina}
                  onChange={(e) => setEditForm({ ...editForm, jina: e.target.value })}
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Kikundi (Makundi)</Form.Label>
                <Select
                  isMulti
                  options={groupOptions}
                  value={editForm.selectedGroups}
                  onChange={(selected) => setEditForm({ ...editForm, selectedGroups: selected || [] })}
                  placeholder="-- Chagua Kikundi --"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Namba *</Form.Label>
                <Form.Control
                  type="text"
                  value={editForm.namba}
                  onChange={(e) => setEditForm({ ...editForm, namba: e.target.value })}
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Ahadi (TZS)</Form.Label>
                <Form.Control
                  type="number"
                  value={editForm.pledge_amount}
                  onChange={(e) => setEditForm({ ...editForm, pledge_amount: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Mchango (TZS)</Form.Label>
                <Form.Control
                  type="number"
                  value={editForm.payment_amount}
                  onChange={(e) => setEditForm({ ...editForm, payment_amount: e.target.value })}
                />
                <Form.Text className="text-muted">
                  Jumla: TZS {parseFloat(editForm.paid_amount || 0).toLocaleString()}
                </Form.Text>
              </Form.Group>
            </div>
          
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Funga
        </Button>
        <Button variant="primary" onClick={handleUpdateGuest} disabled={submitting}>
          {submitting ? <FaSpinner className="fa-spin" /> : 'Hifadhi Mabadiliko'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditGuestModal;
